import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Audio, AVPlaybackStatus } from 'expo-av';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  withRepeat,
  withSequence,
  withDelay,
} from 'react-native-reanimated';
import { FontFamilies } from '@/constants/Typography';
import { Colors } from '@/constants/Colors';

// Audio Player Widget Component
export function AudioPlayerWidget({
  audioUrl,
  duration,
}: {
  audioUrl: string;
  duration?: number;
}) {
  const [sound, setSound] = useState<Audio.Sound | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [position, setPosition] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);

  // Preload audio immediately when component mounts
  useEffect(() => {
    let isMounted = true;
    let loadedSound: Audio.Sound | null = null;

    const preloadAudio = async () => {
      try {
        console.log('[AudioPlayer] Preloading audio from URL:', audioUrl);
        const { sound: newSound } = await Audio.Sound.createAsync(
          { uri: audioUrl },
          { shouldPlay: false },
          (status: AVPlaybackStatus) => {
            if (isMounted && status.isLoaded) {
              setPosition(status.positionMillis || 0);
              setAudioDuration(status.durationMillis || duration || 0);
              if (status.didJustFinish) {
                setIsPlaying(false);
                setPosition(0);
              }
            }
          },
        );
        loadedSound = newSound;
        if (isMounted) {
          console.log('[AudioPlayer] Audio preloaded successfully');
          setSound(newSound);
          setIsLoading(false);
        } else {
          await newSound.unloadAsync();
        }
      } catch (error) {
        console.error('[AudioPlayer] Failed to preload audio:', error);
        console.error('[AudioPlayer] URL was:', audioUrl);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    preloadAudio();

    return () => {
      isMounted = false;
      if (loadedSound) {
        loadedSound.unloadAsync();
      }
    };
  }, [audioUrl, duration]);

  // Cleanup sound on unmount
  useEffect(() => {
    return () => {
      if (sound) {
        sound.unloadAsync();
      }
    };
  }, [sound]);

  const formatDuration = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = async () => {
    if (!sound) {
      console.warn('[AudioPlayer] Sound not loaded yet');
      return;
    }

    if (isPlaying) {
      await sound.pauseAsync();
      setIsPlaying(false);
    } else {
      await sound.playAsync();
      setIsPlaying(true);
    }
  };

  const waveformHeights = [22, 44, 28, 28, 28, 22, 18, 18, 18, 14, 3, 22, 44, 28, 28, 28, 22, 18, 18, 18, 14, 3, 28, 22, 18];

  return (
    <View style={audioStyles.wrapper}>
      <View style={audioStyles.container}>
        <TouchableOpacity
          style={audioStyles.playButton}
          onPress={handlePlayPause}
          disabled={isLoading}
        >
          {isLoading ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Ionicons
              name={isPlaying ? 'pause' : 'play'}
              size={24}
              color="#FFFFFF"
            />
          )}
        </TouchableOpacity>
        <View style={audioStyles.waveformContainer}>
          {waveformHeights.map((height, index) => (
            <View
              key={index}
              style={[audioStyles.waveformBar, { height }]}
            />
          ))}
        </View>
        <View style={audioStyles.durationContainer}>
          <Text style={audioStyles.durationText}>
            {formatDuration(isPlaying ? position : audioDuration)}
          </Text>
        </View>
      </View>
      <View style={audioStyles.voiceChatEndedContainer}>
        <Image
          source={require('@/assets/icons/phone_down.png')}
          style={audioStyles.phoneDownIcon}
        />
        <Text style={audioStyles.voiceChatEndedText}>Voice chat ended</Text>
      </View>
    </View>
  );
}

const audioStyles = StyleSheet.create({
  wrapper: {
    alignSelf: 'flex-start',
    gap: 8,
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.primary,
    borderRadius: 8,
    padding: 8,
    gap: 8,
  },
  playButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.12,
    shadowRadius: 10,
    elevation: 3,
  },
  waveformContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    height: 28,
    overflow: 'hidden',
    width: 130,
  },
  waveformBar: {
    width: 3,
    backgroundColor: '#FFFFFF',
    borderRadius: 3,
  },
  durationContainer: {
    paddingRight: 6,
  },
  durationText: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Medium,
    color: '#FFFFFF',
    textAlign: 'right',
  },
  voiceChatEndedContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  voiceChatEndedText: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: 'rgba(22, 29, 20, 0.5)',
    lineHeight: 23,
  },
  phoneDownIcon: {
    width: 24,
    height: 10,
  },
});

// Animated waveform bar component
function WaveformBar({ delay, height }: { delay: number; height: number }) {
  const scale = useSharedValue(1);

  useEffect(() => {
    scale.value = withDelay(
      delay,
      withRepeat(
        withSequence(
          withTiming(0.4, { duration: 300 }),
          withTiming(1, { duration: 300 }),
        ),
        -1,
        true,
      ),
    );
  }, [delay, scale]);

  const animatedStyle = useAnimatedStyle(() => ({
    height: height * scale.value,
  }));

  return <Animated.View style={[activeCallStyles.bar, animatedStyle]} />;
}

// Active call waveform indicator component
export function ActiveCallWaveform({ isStreaming }: { isStreaming?: boolean }) {
  const barHeights = [22, 44, 28, 28, 28, 22, 18, 18, 18, 14, 3, 22, 44, 28, 28, 28, 22, 18, 18, 18, 14, 3, 28, 22, 18, 18, 18, 14, 3, 22, 44, 28, 28, 28, 22, 18, 18, 18, 14, 3, 44, 28, 18, 18, 18, 14, 3, 44, 28, 18];

  return (
    <View style={activeCallStyles.container}>
      <View style={[activeCallStyles.iconContainer, isStreaming && activeCallStyles.iconContainerStreaming]}>
        <Ionicons name={isStreaming ? 'volume-high' : 'call'} size={20} color={Colors.primary} />
      </View>
      <View style={activeCallStyles.waveformContainer}>
        {barHeights.map((height, index) => (
          <WaveformBar key={index} delay={index * 30} height={height} />
        ))}
      </View>
    </View>
  );
}

const activeCallStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 8,
    height: 64,
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(15, 145, 41, 0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.12,
    shadowRadius: 10,
    elevation: 3,
  },
  iconContainerStreaming: {
    backgroundColor: 'rgba(15, 145, 41, 0.25)',
  },
  waveformContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    height: 48,
    overflow: 'hidden',
  },
  bar: {
    width: 3,
    backgroundColor: Colors.primary,
    borderRadius: 3,
  },
});
